import React, { Component } from 'react';
import { Avatar, Col, Divider, Icon, notification, Row } from 'antd';
import ActionModal from 'common-components/modal/ActionModal';
import { HyperlinkButton, PrimaryButton, SecondaryButton } from 'common-components/buttons';
import { dispatch, IRootDispatch, state } from 'src/stores/rematch/root-store';
import { connect } from 'react-redux';
import { SubTitle, Text, Paragraph } from 'common-components/typography';
import moment from 'moment-timezone';
import CommonUtils from 'utilities/common-utils';
import { timeZone } from 'interfaces/timezone-type';
import { Moment } from 'moment';
import DatePicker from 'react-datepicker';
import { RecurringBookingPattern } from 'utilities/enum-utils';
import _ from 'lodash';
import SpinningLoader from 'common-components/loading/SpinningLoader';
import TextArea from 'antd/es/input/TextArea';
import { BookingAssignWorkerModal } from 'views/bookings-v2/other/components/booking-assign-worker-modal/booking-assign-worker-modal';
import { ICustomerBooking } from 'interfaces/customer-interfaces';
import { ISupportWorker } from 'interfaces/supportWorker-interfaces';
import ConflictCheckUtils from 'utilities/conflict-check-utils';
import { IShiftClashConflict } from 'interfaces/service-interfaces';
import { ISleepoverTimeSlot } from 'interfaces/booking-interfaces';

interface IExtendRecurringBookingsModalProps {
  isOpen: boolean;
  onCloseModal: () => void;
  bookingItem: any;
  bookingRecurringPattern: any;
  doCheckRecurringPattern: typeof dispatch.bookingsStore.doCheckRecurringPattern;
  doCheckWorker: typeof dispatch.bookingsStore.doCheckWorker;
  doExtendRecurrence: typeof dispatch.bookingsStore.doExtendRecurrence;
  displayTimezone: timeZone;
  recurringBookings: typeof state.bookingsStore.recurringBookingList;
}

interface IExtendRecurringBookingsModalState {
  isLoading: boolean;
  title: string;
  step: number;
  startDateTimeWithoutTz: Moment;
  endDateTimeWithoutTz: Moment;
  recurringToWithoutTz: Moment;
  extendedEndDate: Moment;
  isErrorExtendedDate: boolean;
  canGoNext: boolean;
  newBookings: ICustomerBooking[];
  isCalculatingNewBooking: boolean;
  selectedWorker: ISupportWorker;
  openAssignWorker: boolean;
  conflicts: any;
  instruction: any;
  bookingsOutsideAvailabilityCanBeAssigned: string[];
  bookingsOutsideAvailability: any[];
  shiftClashConflicts: IShiftClashConflict[];
  ignoreShiftClashShiftIds: string[];
  sleepoverTimeSlots?: ISleepoverTimeSlot[];
}

class ExtendRecurringBookingsModal extends Component<
  IExtendRecurringBookingsModalProps,
  IExtendRecurringBookingsModalState
> {
  state = {
    isLoading: false,
    title: 'Edit recurring series',
    step: 1,
    startDateTimeWithoutTz: null,
    endDateTimeWithoutTz: null,
    recurringToWithoutTz: null,
    extendedEndDate: null,
    isErrorExtendedDate: false,
    canGoNext: false,
    newBookings: null,
    isCalculatingNewBooking: false,
    selectedWorker: null,
    openAssignWorker: false,
    conflicts: null,
    instruction: null,
    bookingsOutsideAvailabilityCanBeAssigned: [],
    bookingsOutsideAvailability: [],
    shiftClashConflicts: [],
    ignoreShiftClashShiftIds: [],
    sleepoverTimeSlots: [],
  };

  private _onCloseModal = () => {
    this.props.onCloseModal();
    this.setState({
      isLoading: false,
      step: 1,
      title: 'Edit recurring series',
      startDateTimeWithoutTz: null,
      endDateTimeWithoutTz: null,
      recurringToWithoutTz: null,
      extendedEndDate: null,
      isErrorExtendedDate: false,
      canGoNext: false,
      newBookings: null,
      isCalculatingNewBooking: false,
      selectedWorker: null,
      openAssignWorker: false,
      conflicts: null,
      instruction: null,
      bookingsOutsideAvailabilityCanBeAssigned: [],
      bookingsOutsideAvailability: [],
      shiftClashConflicts: [],
      ignoreShiftClashShiftIds: [],
    });
  };

  private _checkTeamMemberConflicts = async () => {
    const { bookingItem } = this.props;
    if (this.state.selectedWorker) {
      const result: any = await this.props.doCheckWorker({
        serviceId: bookingItem.serviceId,
        workerId: this.state.selectedWorker.supportWorkerId,
        address: bookingItem.address
          ? {
              ...bookingItem.address,
              geoLat: Number(bookingItem.address.geoLat),
              geoLng: Number(bookingItem.address.geoLng),
            }
          : null,
        sessions: this.state.newBookings,
        customerUserId: bookingItem.bookerUserId,
      });
      this.setState({
        conflicts: result.data,
        step: 4,
        title: 'Edit recurring series',
      });
    } else {
      this.setState({
        conflicts: null,
        step: 4,
        title: 'Edit recurring series',
      });
    }
  };

  private _checkNewEndDateValidity = (e) => {
    const { bookingRecurringPattern } = this.props;
    const { startDateTimeWithoutTz } = this.state;
    if (!e) {
      this.setState({ extendedEndDate: undefined, newBookings: null, canGoNext: false });
    } else if (
      bookingRecurringPattern.recurringPattern === RecurringBookingPattern.EveryDay &&
      moment(e) > moment(startDateTimeWithoutTz).add(1, 'year')
    ) {
      this.setState({ extendedEndDate: moment(e), isErrorExtendedDate: true, canGoNext: false, newBookings: null });
    } else if (moment(e) > moment(startDateTimeWithoutTz).add(3, 'year')) {
      this.setState({ extendedEndDate: moment(e), isErrorExtendedDate: true, canGoNext: false, newBookings: null });
    } else {
      this.setState(
        {
          extendedEndDate: moment(e),
          isErrorExtendedDate: false,
          canGoNext: true,
        },
        () => this._calculateNumberOfNewRecurrences(),
      );
    }
  };

  private _calculateNumberOfNewRecurrences = async () => {
    const { bookingRecurringPattern, doCheckRecurringPattern, bookingItem, recurringBookings } = this.props;
    const { extendedEndDate, recurringToWithoutTz } = this.state;

    const startDateTimeLastBooking = moment(_.last(recurringBookings).startDateTime);
    const endDateTimeLastBooking = moment(_.last(recurringBookings).endDateTime);

    // Calculate the next booking date based on the recurrence pattern
    let newStartDateTime: moment.Moment;
    switch (bookingRecurringPattern.recurringPattern) {
      case RecurringBookingPattern.EveryDay:
        newStartDateTime = moment(startDateTimeLastBooking).add(1, 'day');
        break;
      case RecurringBookingPattern.EveryWeek:
        newStartDateTime = moment(startDateTimeLastBooking).add(1, 'week');
        break;
      case RecurringBookingPattern.EveryFortnight:
        newStartDateTime = moment(startDateTimeLastBooking).add(2, 'weeks');
        break;
      case RecurringBookingPattern.EveryFourWeeks:
        newStartDateTime = moment(startDateTimeLastBooking).add(4, 'weeks');
        break;
      case RecurringBookingPattern.EveryThreeWeeks:
        newStartDateTime = moment(startDateTimeLastBooking).add(3, 'weeks');
        break;
      case RecurringBookingPattern.Monthly:
        newStartDateTime = moment(startDateTimeLastBooking).add(1, 'month');
        break;
      default:
        throw new Error(`Unsupported recurring pattern: ${bookingRecurringPattern.recurringPattern}`);
    }

    const newOldDateTime = moment(newStartDateTime).add(
      moment(endDateTimeLastBooking).diff(startDateTimeLastBooking, 'seconds'),
      'seconds',
    );

    this.setState({ isCalculatingNewBooking: true });

    const startDateTimeWithFormat = moment.tz(newStartDateTime, bookingItem.timezone).toISOString();
    const rangeDate = moment(startDateTimeWithFormat).diff(
      moment(_.last(recurringBookings).startDateTime),
      'days',
      true,
    );
    const newSleepoverTimeSlots = _.map(_.last(recurringBookings)?.sleepoverTimeSlots, (sleepover) => ({
      startDateTime: moment
        .tz(moment(sleepover.startDateTime).add(rangeDate, 'days'), bookingItem.timezone)
        .toISOString(),
      endDateTime: moment.tz(moment(sleepover.endDateTime).add(rangeDate, 'days'), bookingItem.timezone).toISOString(),
    }));

    try {
      const result: any = await doCheckRecurringPattern({
        serviceId: bookingItem.serviceId,
        startDateTime: moment.tz(newStartDateTime, bookingItem.timezone).toISOString(),
        endDateTime: moment.tz(newOldDateTime, bookingItem.timezone).toISOString(),
        recurringPattern: bookingRecurringPattern.recurringPattern,
        recurringTo: moment.tz(extendedEndDate, bookingItem.timezone).toISOString(),
        serviceDirection: bookingItem.serviceDirection,
        timezone: bookingItem.timezone,
        address: bookingItem.address
          ? {
              ...bookingItem.address,
              geoLat: Number(bookingItem.address.geoLat),
              geoLng: Number(bookingItem.address.geoLng),
            }
          : null,
        userId: bookingItem.bookerUserId,
        sleepoverType: _.last(recurringBookings)?.sleepoverType,
        sleepoverTimeSlots: newSleepoverTimeSlots,
      });
      this.setState({
        isCalculatingNewBooking: false,
        newBookings: result.data.sessions,
        canGoNext: result.data.sessions && result.data.sessions.length > 0,
        sleepoverTimeSlots: result.data?.sleepoverTimeSlotList,
      });
    } catch (e) {
      this.setState({
        isCalculatingNewBooking: false,
        newBookings: [],
        canGoNext: false,
        sleepoverTimeSlots: [],
      });
    }
  };

  private _openAssignWorkerModal = () => {
    this.setState({ openAssignWorker: true });
  };

  private _closeAssignWorkerModal = () => this.setState({ openAssignWorker: false });

  private _onSelectSupportWorker = async ({
    selectedWorker,
    bookingOutSideCanBeAssign = [],
    bookingOutsideAvailability = [],
    shiftClashConflicts = [],
    ignoreShiftClashShiftIds = [],
  }) => {
    this.setState({
      bookingsOutsideAvailability: bookingOutsideAvailability,
      bookingsOutsideAvailabilityCanBeAssigned: bookingOutSideCanBeAssign,
      selectedWorker,
      shiftClashConflicts,
      ignoreShiftClashShiftIds,
    });
  };

  private _onExtendRecurrence = async () => {
    const { bookingItem, recurringBookings } = this.props;
    const {
      newBookings,
      selectedWorker,
      instruction,
      extendedEndDate,
      bookingsOutsideAvailabilityCanBeAssigned,
      ignoreShiftClashShiftIds,
      sleepoverTimeSlots,
    } = this.state;
    this.setState({ isLoading: true });
    try {
      const timeSlotsOutsideCanBeAssignedIds = _.map(
        bookingsOutsideAvailabilityCanBeAssigned,
        (serviceDateTime) => serviceDateTime.serviceDateTimeId,
      );
      await this.props.doExtendRecurrence({
        bookingId: bookingItem.bookingId,
        newEndDate: moment.tz(moment(extendedEndDate).format('YYYY-MM-DD HH:mm'), bookingItem.timezone),
        bookerUserId: bookingItem.bookerUserId,
        serviceId: bookingItem.serviceId,
        paymentSourceType: bookingItem.paymentSourceType,
        timeSlots: newBookings,
        timeSlotsOutsideCanBeAssigned: timeSlotsOutsideCanBeAssignedIds,
        supportWorkerId: selectedWorker && selectedWorker.supportWorkerId,
        instruction,
        ignoreShiftClashShiftIds,
        sleepoverType: _.last(recurringBookings)?.sleepoverType,
        sleepoverTimeSlots,
      });
      this.setState({ isLoading: false, step: 5, title: 'Booking successfully created!' });
    } catch (e) {
      this.setState({ isLoading: false });
      notification.error({ message: 'Oops! Something went wrong, please try again.' });
    }
  };

  private _goToFirstStep = () => {
    this.setState({ step: 1 });
  };

  private _updateInstruction = (event) => {
    this.setState({ instruction: event.target.value });
  };

  componentDidUpdate(prevProps: Readonly<IExtendRecurringBookingsModalProps>) {
    if (this.props.bookingRecurringPattern && !prevProps.isOpen && this.props.isOpen) {
      this.setState({
        startDateTimeWithoutTz: moment(
          moment
            .tz(
              this.props.bookingRecurringPattern && this.props.bookingRecurringPattern.startDateTime,
              this.props.bookingItem.timezone,
            )
            .format('YYYY-MM-DD HH:mm'),
        ),
        endDateTimeWithoutTz: moment(
          moment
            .tz(
              this.props.bookingRecurringPattern && this.props.bookingRecurringPattern.endDateTime,
              this.props.bookingItem.timezone,
            )
            .format('YYYY-MM-DD HH:mm'),
        ),
        recurringToWithoutTz: moment(
          moment
            .tz(
              this.props.bookingRecurringPattern && this.props.bookingRecurringPattern.recurringTo,
              this.props.bookingItem.timezone,
            )
            .format('YYYY-MM-DD HH:mm'),
        ),
      });
    }
  }

  render() {
    const { bookingRecurringPattern, bookingItem, displayTimezone } = this.props;
    const {
      isLoading,
      title,
      step,
      startDateTimeWithoutTz,
      recurringToWithoutTz,
      extendedEndDate,
      isErrorExtendedDate,
      isCalculatingNewBooking,
      newBookings,
      selectedWorker,
      conflicts,
      instruction,
      bookingsOutsideAvailabilityCanBeAssigned,
      bookingsOutsideAvailability,
      shiftClashConflicts,
      ignoreShiftClashShiftIds,
    } = this.state;

    const shiftClashShiftIds = !_.isEmpty(shiftClashConflicts)
      ? _.map(shiftClashConflicts[0].selectedShifts, (shift) => shift.serviceDateTimeId)
      : [];

    const ignoreOutsideAvailabilityShiftIds = _.map(
      bookingsOutsideAvailabilityCanBeAssigned,
      (booking) => booking.serviceDateTimeId,
    );
    const outsideAvailabilityShiftIds = _.map(bookingsOutsideAvailability, (booking) => booking.serviceDateTimeId);

    const allConflictingShifts = _.union(outsideAvailabilityShiftIds, shiftClashShiftIds);
    const bookingsNotSelectedToBeAssignedIds = ConflictCheckUtils.filterIgnoredConflictedShiftIds(
      allConflictingShifts,
      ignoreShiftClashShiftIds,
      ignoreOutsideAvailabilityShiftIds,
      shiftClashShiftIds,
      outsideAvailabilityShiftIds,
    );

    if (!bookingRecurringPattern) {
      return <></>;
    }

    const numberOfAssignedBookings =
      conflicts &&
      _.filter(
        newBookings,
        (booking: any) =>
          !_.find(
            conflicts.confirmedShiftConflicts,
            (conflict) =>
              moment(booking.startDateTime).format('YYYY-MM-DD HH:mm') ===
              moment(conflict.startDateTime).format('YYYY-MM-DD HH:mm'),
          ),
      ).length;

    return (
      <div>
        {this.state.openAssignWorker && (
          <BookingAssignWorkerModal
            isOpen={this.state.openAssignWorker}
            onClose={this._closeAssignWorkerModal}
            onSubmitAssign={this._onSelectSupportWorker}
            serviceId={bookingItem.serviceId}
            isRecurring={bookingItem.isRecurring}
            address={bookingItem.address}
            customerUserIds={[bookingItem.bookerUserId]}
            startDateTime={bookingItem.startDateTime}
            endDateTime={bookingItem.endDateTime}
            sessions={newBookings}
            noValidationStep={true}
            isAssignedTeamMemberConfirmed={true}
            bookingType={bookingItem.bookingType}
            displayTimezone={displayTimezone}
          />
        )}
        <ActionModal isOpen={this.props.isOpen} title={title} onClose={this._onCloseModal} width='x-large'>
          {step === 1 && (
            <>
              <div>
                <Row className={'mb-large'}>
                  <Col span={24}>
                    <SubTitle>Recurring Pattern</SubTitle>
                    {bookingRecurringPattern &&
                      CommonUtils.getRecurringPatternLabel(bookingRecurringPattern.recurringPattern)}
                  </Col>
                </Row>
                <Row className={'mb-large'}>
                  <Col span={12}>
                    <SubTitle>Start date</SubTitle>
                    {moment(startDateTimeWithoutTz).format('DD/MM/YYYY')}
                  </Col>
                  <Col span={12}>
                    <SubTitle>Current end date</SubTitle>
                    {moment(recurringToWithoutTz).format('DD/MM/YYYY')}
                  </Col>
                </Row>
                <Row className={'mb-large'}>
                  <Col span={24}>
                    <SubTitle>Extend series until</SubTitle>
                    <DatePicker
                      className={`gh-datepicker-large ${isErrorExtendedDate && 'border-red-dark'}`}
                      showPopperArrow={false}
                      placeholderText={'Select a date'}
                      selected={extendedEndDate && extendedEndDate.toDate()}
                      minDate={
                        recurringToWithoutTz && moment(recurringToWithoutTz).add(1, 'day').startOf('day').toDate()
                      }
                      calendarClassName='gh-datepicker-calendar'
                      dateFormat='d/M/yyyy'
                      onChange={this._checkNewEndDateValidity}
                    />
                    <br />
                    {isErrorExtendedDate && (
                      <Text color={'red-dark'}>
                        You cannot extend the date of this series for{' '}
                        <b>more than {bookingRecurringPattern.recurringPattern === 1 ? '1 year' : '3 years'}</b>
                      </Text>
                    )}
                    {newBookings &&
                      !isCalculatingNewBooking &&
                      (newBookings.length === 0 ? (
                        <Text>This change does not add any new booking.</Text>
                      ) : (
                        <Text>
                          This change will create <b>{newBookings.length}</b> new booking
                          {newBookings.length !== 1 && 's'}
                        </Text>
                      ))}
                  </Col>
                </Row>
                {isCalculatingNewBooking ? (
                  <SpinningLoader size={100} message={'Calculating new bookings...'} />
                ) : (
                  newBookings && (
                    <>
                      <div className={'mb-large'}>
                        <SubTitle>Team member (optional)</SubTitle>
                        {selectedWorker ? (
                          <div className={'flex-row align-center'}>
                            <Avatar
                              icon='user'
                              className='small mr-small'
                              shape='square'
                              src={selectedWorker.attachmentUrl}
                            />
                            <Text>{selectedWorker.firstName + ' ' + selectedWorker.lastName}</Text>
                          </div>
                        ) : (
                          <div>
                            <Icon type={'warning'} className={'mr-small text-color-warning-orange'} theme={'filled'} />
                            No team member selected
                          </div>
                        )}
                        <div className={'mt-x-small'}>
                          <HyperlinkButton onClick={this._openAssignWorkerModal}>Select Team member</HyperlinkButton>
                        </div>
                      </div>
                      <div className={'mb-large'}>
                        <SubTitle>Instructions (optional)</SubTitle>
                        <TextArea
                          placeholder={'Add instructions here...'}
                          value={instruction}
                          autoSize={{ minRows: 4, maxRows: 20 }}
                          onChange={this._updateInstruction}
                        />
                      </div>
                    </>
                  )
                )}
              </div>
              <div className={'mb-small'}>
                <Row type={'flex'} justify={'end'}>
                  <SecondaryButton className='mr-medium' size='large' onClick={this._onCloseModal}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton size='large' disabled={!this.state.canGoNext} onClick={this._checkTeamMemberConflicts}>
                    Next
                  </PrimaryButton>
                </Row>
              </div>
            </>
          )}
          {step === 4 && (
            <>
              <div className='mb-medium'>
                <Text>
                  As a result of the changes made to the series the following new bookings will be created up till{' '}
                  <b>{moment(extendedEndDate).format('DD/MM/YYYY')}</b>.
                </Text>
              </div>
              <SubTitle>
                {newBookings.length} new booking{newBookings.length !== 0 && 's'} will be created.
              </SubTitle>
              <Row className='text-color-secondary text-uppercase pt-medium'>
                <Col span={8} className='ph-medium'>
                  <Text weight={'bold'}>Date</Text>
                </Col>
                <Col span={8} className='ph-medium'>
                  <Text weight={'bold'}>Status</Text>
                </Col>
                <Col span={8} className='ph-medium'>
                  <Text weight={'bold'}>Team member</Text>
                </Col>
              </Row>
              <Divider className='mv-medium mb-none' />
              <div style={{ overflow: 'auto', maxHeight: '35vh' }} className={'mb-large'}>
                {_.map(newBookings, (booking) => {
                  const hasWorkerAssigned =
                    selectedWorker &&
                    !_.find(
                      bookingsNotSelectedToBeAssignedIds,
                      (serviceDateTimeId) => serviceDateTimeId === booking.serviceDateTimeId,
                    );
                  return (
                    <SummaryResult
                      booking={booking}
                      hasWorkerAssigned={hasWorkerAssigned}
                      selectedWorker={selectedWorker}
                      timezone={displayTimezone}
                    />
                  );
                })}
              </div>
              <div className={'mb-small'}>
                <Row type={'flex'} justify={'end'}>
                  <SecondaryButton className='mr-medium' size='large' onClick={this._goToFirstStep}>
                    Previous
                  </SecondaryButton>
                  <PrimaryButton size='large' onClick={this._onExtendRecurrence} loading={isLoading}>
                    Confirm
                  </PrimaryButton>
                </Row>
              </div>
            </>
          )}
          {step === 5 && (
            <>
              <div className='mb-small'>
                <Paragraph>A new recurring booking has been successfully created.</Paragraph>
                <Text size={'x-large'}>{bookingItem.serviceName}</Text>
              </div>
              <div className={'bg-quaternary p-medium rounded-big mb-x2-large'}>
                <div className={'mb-large'}>
                  <SubTitle>Summary</SubTitle>
                  <Text>
                    This recurring series has been extended until <b>{moment(extendedEndDate).format('DD/MM/YYYY')}</b>,
                    for a total of <b>{newBookings.length}</b> new booking{newBookings.length !== 1 && 's'}.
                  </Text>
                </div>
                <div className={'mb-large'}>
                  <SubTitle>Team member</SubTitle>
                  {selectedWorker ? (
                    <div className={'flex-row align-center'}>
                      <Avatar
                        icon='user'
                        className='small mr-small'
                        shape='square'
                        src={selectedWorker.attachmentUrl}
                      />
                      <div className={'mr-small'}>
                        <Text>{selectedWorker.firstName + ' ' + selectedWorker.lastName}</Text>
                      </div>
                      {numberOfAssignedBookings &&
                        numberOfAssignedBookings < newBookings.length &&
                        `(${numberOfAssignedBookings}/${newBookings.length} bookings)`}
                    </div>
                  ) : (
                    <div>
                      <Icon type={'warning'} className={'mr-small text-color-warning-orange'} theme={'filled'} />
                      No team member selected
                    </div>
                  )}
                </div>
                <div className={'mb-large'}>
                  <SubTitle>Instructions</SubTitle>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{instruction ? instruction : '-'}</Text>
                </div>
                <div className={'mb-large'}>
                  <SubTitle>New booking created</SubTitle>
                  <Text>{newBookings && newBookings.length}</Text>
                </div>
              </div>
              <div className={'mb-small'}>
                <Row type={'flex'} justify={'end'}>
                  <PrimaryButton className='mr-medium' size='large' onClick={this._onCloseModal}>
                    Close
                  </PrimaryButton>
                </Row>
              </div>
            </>
          )}
        </ActionModal>
      </div>
    );
  }
}

interface ISummaryResultProps {
  booking: any;
  hasWorkerAssigned: boolean;
  selectedWorker: any;
  timezone: timeZone;
}

function SummaryResult(props: ISummaryResultProps) {
  const { booking, timezone, hasWorkerAssigned, selectedWorker } = props;

  return (
    <Row className='pv-medium evenodd' type={'flex'} align={'middle'}>
      <Col span={8} className={'ph-medium'}>
        <Text>
          <b>{moment.tz(booking.startDateTime, timezone).format('D MMMM YYYY')}</b>
          <br />
          {moment.tz(booking.startDateTime, timezone).format('hh:mm A')} -{' '}
          {moment.tz(booking.endDateTime, timezone).format('hh:mm A')}
        </Text>
      </Col>
      <Col span={8} className={'ph-medium'}>
        Confirmed
      </Col>
      <Col span={8} className={'ph-medium'}>
        {hasWorkerAssigned ? (
          <div className={'flex-row align-center'}>
            <Avatar icon='user' className='small mr-small' shape='square' src={selectedWorker.attachmentUrl} />
            <Text>{selectedWorker.firstName + ' ' + selectedWorker.lastName}</Text>
          </div>
        ) : (
          <Text color={'red-dark'}>No assigned team member</Text>
        )}
      </Col>
    </Row>
  );
}

const mapDispatch = (dispatch: IRootDispatch) => ({
  doCheckRecurringPattern: dispatch.bookingsStore.doCheckRecurringPattern,
  doCheckWorker: dispatch.bookingsStore.doCheckWorker,
  doExtendRecurrence: dispatch.bookingsStore.doExtendRecurrence,
});

export default connect(null, mapDispatch)(ExtendRecurringBookingsModal);
